import { Button, Container } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { ChevronRight, Info } from "@material-ui/icons";
import { Events } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

import { defaultSessionEnd, defaultSessionStart } from "../../constants";
import { Action, EventProps } from "../../types/StaticTypes";
import { createRenderingInfoPrinter, triggerDebuggerAndThrow } from "../../utils/debugging-utils";
import { toLocalTime } from "../../utils/time-utils";
import { TimePickerModal } from "../Modals";
import ScheduleDetails from "./ScheduleDetails";
import { ScheduleState } from "./SchedulesListings";

const printRenderingInfo = createRenderingInfoPrinter("SchedulesAvailability", false);

const disabled = {
  background: "none",
  color: "var(--text-heading)",
  backgroundColor: "#dc354594",
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    container: {
      marginTop: -38,
      padding: window.innerWidth < 700 ? 0 : "",
      [theme.breakpoints.up("md")]: {
        marginTop: 38,
      },
      "& .agenda-section .disabled": {
        cursor: "not-allowed",
        "& .MuiPaper-root": disabled,
        "& .MuiContainer-root.break h5": disabled,
        "& .MuiContainer-root.add h5": disabled,
        "& .MuiContainer-root.blocked h5": disabled,
        "& .MuiContainer-root.lunch h5": disabled,
      },
      "& button": {
        marginTop: 5,
        borderRadius: 0,
        backgroundColor: "var(--text-primary)",
        textTransform: "none",
        height: 52,
        fontSize: 16,
        fontWeight: 500,
        "&:disabled": {
          backgroundColor: "var(--text-primary)",
        },
        "&:hover": {
          backgroundColor: "var(--text-primary)",
        },
        "& span:first-child > span": {
          marginLeft: 15,
        },
        "& span:first-child > svg": {
          marginLeft: "calc(100% - 230px)",
          marginTop: -2,
        },
      },
      "& .agenda-section .session-blocked-time": {
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        padding: "60px 80px",
        height: "30vh",
        justifyContent: "center",
        textAlign: "center",
        textTransform: "none",
        fontSize: 16,
        fontWeight: 500,
        color: "#595959",
        background: "repeating-linear-gradient(45deg, #d3d3d3, #ffffff 4px)",
        "& div": {
          width: "100%",
          "& svg": {
            height: 32,
            width: 32,
            fill: "#333333",
          },
        },
      },
    },
    startEndDate: {
      whiteSpace: "pre",
    },
  })
);

type Availability = ScheduleState["availability"];

type ScheduleAvailabilityProps = {
  children?: any;
  agenda?: Events.IAgenda[];
  availability: Availability;
  event?: Events.IAgenda | EventProps;
  session?: Events.ISession;
  dispatch: React.Dispatch<Action>;
};

function isEventProps(e: Events.IAgenda | EventProps): e is EventProps {
  return (e as EventProps).allDay !== undefined;
}

function createAvailabilityString(availability: Availability) {
  return `${availability.start.replace("Z", "")} - ${availability.end.replace("Z", "")}`;
}

function getDayPart(evStartTime: string) {
  return evStartTime.split(" ")[0];
}

function createIsoStringFromTimeAndEventStartWithFallback(
  time: string,
  evStartTime: string,
  fallback: string
) {
  const value = time !== null ? time : fallback;

  return `${getDayPart(evStartTime)}T${value}Z`;
}

export default function ScheduleAvailability({
  agenda,
  availability,
  event,
  session,
  dispatch,
}: ScheduleAvailabilityProps) {
  let eventProps;

  if (event && isEventProps(event)) eventProps = event;

  const { t } = useTranslation();
  const classes = useStyles();
  const [showAvail, setShowAvail] = React.useState(false);
  const [startAndEndTimes, setStartAndEndTimes] = React.useState<{
    start: string;
    end: string;
  } | null>(null);
  const [allDay, setAllDay] = React.useState(false);

  const generateSessionBlock = (currentEvent: Events.IAgenda, i: number) => {
    return currentEvent.session_type ? (
      <div
        key={i}
        style={{ margin: 0, padding: 0 }}
        className={`session-${currentEvent.session_type.toString().toLowerCase()} ${
          currentEvent.is_editable ? "" : "disabled"
        }`}
      >
        <ScheduleDetails event={currentEvent} session={session} dispatch={dispatch} />
      </div>
    ) : (
      <></>
    );
  };
  // Toggle Event handler
  const handleToggle = () => {
    setAllDay(!allDay);
    if (allDay) {
      const temp1 = document.getElementById("demo-from") as HTMLElement;
      const temp2 = document.getElementById("demo-to") as HTMLElement;

      temp1.innerHTML = toLocalTime(defaultSessionStart);
      temp2.innerHTML = toLocalTime(defaultSessionEnd);
    }
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const from = formData.get("from") as string;
    const to = formData.get("to") as string;
    if (!event) return;

    if (isEventProps(event)) {
      console.error("This is not supposed to happen, at least according to Ninestack's logic");
      triggerDebuggerAndThrow();
      return;
    }

    // pretty sure the below logic can be further simplified, but needs more tests first.

    const evStartTime = event.ev_start_time;
    const payload = {
      start: createIsoStringFromTimeAndEventStartWithFallback(from, evStartTime, "08:00"),
      end: createIsoStringFromTimeAndEventStartWithFallback(to, evStartTime, "08:00"),
      name: event.name,
      type: "AVAILABILITY_PATCH",
    };

    const start = `${getDayPart(evStartTime)}T${defaultSessionStart}`;

    if (!allDay) {
      if (payload.start === payload.end) {
        payload.start = start;
        payload.end = `${getDayPart(evStartTime)}T${defaultSessionEnd}`;
      }
    } else {
      payload.start = start;
      payload.end = start;
    }

    dispatch({ type: "update-availability", payload });
    return setStartAndEndTimes(null);
  };
  const startAndEndAvailabilityDiffers = availability.start !== availability.end;

  React.useEffect(() => {
    if (!startAndEndTimes) {
      setShowAvail(false);
    } else if (startAndEndTimes) {
      setShowAvail(true);
    }
    setAllDay(!startAndEndAvailabilityDiffers);
  }, [startAndEndTimes, startAndEndAvailabilityDiffers, setShowAvail]);

  printRenderingInfo();

  if (!availability || !agenda) {
    return (
      <Container className={classes.container}>
        <></>
      </Container>
    );
  }

  function renderButton(eventProps?: EventProps) {
    const allDay = eventProps?.allDay;
    const unavailableResource = eventProps && eventProps.resource && !eventProps.resource.available;

    return (
      <Button
        color="primary"
        variant="contained"
        fullWidth
        disabled={allDay}
        onClick={() => setStartAndEndTimes(availability)}
      >
        {!allDay && !unavailableResource && (
          <>
            {startAndEndAvailabilityDiffers ? (
              <>
                {t("Availability")}
                <span className={classes.startEndDate}>
                  {createAvailabilityString(availability)}
                </span>
              </>
            ) : (
              <>
                <span style={{ marginLeft: 5 }}>{t("Update")}</span>
                <span style={{ marginLeft: 5 }}>{t("availability")}</span>
              </>
            )}
            <ChevronRight />
          </>
        )}
      </Button>
    );
  }

  return (
    <Container className={classes.container}>
      {renderButton(eventProps)}

      <section className="agenda-section">
        {startAndEndAvailabilityDiffers ? (
          agenda.map(generateSessionBlock)
        ) : (
          <div className="session-blocked-time">
            <div>
              <Info />
            </div>
            {eventProps?.allDay ? eventProps.title : t("Not available")}
          </div>
        )}
      </section>

      {startAndEndTimes && (
        <TimePickerModal
          centered
          show={showAvail}
          toggle={allDay}
          title={t("Availability")}
          onToggle={handleToggle}
          currentItem={startAndEndTimes}
          onHide={() => setStartAndEndTimes(null)}
          onSubmit={handleSubmit}
        />
      )}
    </Container>
  );
}
